import { useState } from "react"
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom"
import { ArrowLeft } from "lucide-react"
import { ConfigPropertiesPanel, type ConfigProperty } from "@/components/alfred/config-properties-panel"
import { DashboardHeader } from "@/components/shared/dashboard-header"
import { ApiErrorState, DeviceNotFoundState } from "@/components/shared/empty-states"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { useSmithersDevice } from "@/hooks/use-smithers-devices"
import { formatDateTimeWithZone } from "@/lib/utils"
import { isNotFoundError } from "@/types/api"
import type { SmithersDeviceConfig } from "@/types/smithers-device"
import { getSmithersDeviceListOrigin, smithersDeviceListPath } from "@/lib/smithers-device-navigation"

function deviceProperties(device: SmithersDeviceConfig): ConfigProperty[] {
  const inherited = new Set(device.inheritedFromGlobal)
  return [
    ...([
      ["id", "Record ID"], ["clientId", "Client ID"],
      ["deviceId", "Device ID"], ["sotiDeviceId", "SOTI Device ID"],
    ] as const).map(([key, label]): ConfigProperty => ({ key, label, section: "Device Identification", type: "readonly", value: String(device[key] || "—") })),
    { key: "validationVendor", label: "Validation Vendor", section: "Device Configuration", type: "enum", value: device.validationVendor, isInherited: inherited.has("validationVendor"), options: [{ value: "ProVenue", label: "ProVenue" }, { value: "Orion", label: "Orion" }] },
    ...([
      ["facilityCode", "Facility Code"], ["venueId", "Venue ID"], ["areaCode", "Area Code"],
      ["deviceCode", "Device Code"], ["nteClubCode", "NTE Club Code"],
    ] as const).map(([key, label]): ConfigProperty => ({ key, label, section: "Device Configuration", type: "string", value: device[key], isInherited: inherited.has(key) })),
    { key: "matchThreshold", label: "Match Threshold", section: "Device Configuration", type: "number", value: device.matchThreshold, isInherited: inherited.has("matchThreshold") },
    { key: "validationTimeoutMs", label: "Validation Timeout (ms)", section: "Device Configuration", type: "number", value: device.validationTimeoutMs, isInherited: inherited.has("validationTimeoutMs") },
    { key: "proVenueInstanceId", label: "ProVenue Instance", section: "ProVenue Connection", type: "enum", value: device.proVenueInstanceId === null ? "none" : String(device.proVenueInstanceId), isInherited: inherited.has("proVenueInstanceId"), options: [{ value: "none", label: "None" }, ...(device.proVenueInstanceId === null ? [] : [{ value: String(device.proVenueInstanceId), label: `${device.proVenueUser || "Instance"} (${device.proVenueInstanceId})` }])] },
    { key: "proVenueUser", label: "ProVenue User", section: "ProVenue Connection", type: "readonly", value: device.proVenueUser || "None" },
    { key: "proVenueHost", label: "ProVenue Host", section: "ProVenue Connection", type: "readonly", value: device.proVenueInstances[0]?.hostName || "—" },
    { key: "createdAt", label: "Created At", section: "Audit", type: "readonly", value: formatDateTimeWithZone(device.createdAt) },
    { key: "updatedAt", label: "Updated At", section: "Audit", type: "readonly", value: formatDateTimeWithZone(device.updatedAt) },
    { key: "updatedBy", label: "Updated By", section: "Audit", type: "readonly", value: device.updatedBy },
  ]
}

export function SmithersDeviceDetailPage() {
  const { id } = useParams()
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()
  const [origin] = useState(() => getSmithersDeviceListOrigin(location.state))
  const numericId = Number(id)
  const validId = Number.isSafeInteger(numericId) && numericId > 0
  const detail = useSmithersDevice(validId ? numericId : undefined)
  const device = detail.data
  const clientId = origin ? origin.clientId : params.get("clientId")
  const backPath = smithersDeviceListPath(clientId)
  const backLabel = clientId === null ? "Back to Devices" : `Back to ${clientId} devices`
  const returnToDevices = () => navigate(backPath, origin ? { replace: true, state: { smithersDeviceList: origin } } : undefined)
  const openEdit = () => navigate(`/smithers/devices/${numericId}/edit${clientId === null ? "" : `?${new URLSearchParams({ clientId })}`}`, {
    state: origin ? { fromSmithersDeviceList: true, smithersDeviceList: origin } : undefined,
  })

  return <>
    <DashboardHeader />
    <main className="container mx-auto flex flex-col gap-6 px-4 py-6">
      <div>
        <Button asChild variant="ghost" size="sm" className="-ml-2 mb-4 text-muted-foreground">
          <Link to={backPath} onClick={(event) => { if (origin && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) { event.preventDefault(); returnToDevices() } }}><ArrowLeft data-icon="inline-start" />{backLabel}</Link>
        </Button>
        <h1 className="text-2xl font-semibold text-foreground">
          {detail.isLoading ? <Skeleton className="h-8 w-56" aria-label="Loading device name" /> : device?.deviceId ?? `Device ${id}`}
        </h1>
        {device && <p className="mt-1 text-sm text-muted-foreground">
          Client: {device.clientId}{device.venueId && ` · Venue: ${device.venueId}`}
          {device.inheritedFromGlobal.length > 0 && ` · ${device.inheritedFromGlobal.length} fields inherited from global`}
          {` · Updated ${formatDateTimeWithZone(device.updatedAt)} by ${device.updatedBy}`}
        </p>}
      </div>
      {!validId || isNotFoundError(detail.error) ? <DeviceNotFoundState onBack={returnToDevices} actionLabel={backLabel} />
        : detail.isLoading ? <Skeleton className="h-64 w-full" aria-label="Loading device configuration" />
        : detail.error || !device ? <ApiErrorState onRetry={() => void detail.mutate()} />
        : <ConfigPropertiesPanel className="mx-0 [&_[data-field-key]]:grid-cols-1 [&_[data-field-key]]:items-start [&_[data-field-key]]:gap-2.5" properties={deviceProperties(device)} resetKey={device} onEdit={openEdit} onSave={openEdit} configurationTitle="Device configuration" saveConfirmationTitle="Save device configuration changes?" saveConfirmationDescription="Save changes to this device." />}
    </main>
  </>
}
